import type { Resource } from 'i18next';
import React from 'react';

import type { LocaleResource } from './../../ricos-types';
import { i18next } from './../../wix-rich-content-common';

import viewerVitalTranslations from '../translations/vitalTranslations.json';
import type { ContentViewerProps } from '../types';
import ContentViewer from './ContentViewer';

/**
 * The ricos-viewer is facing a few issues with translations in SSR. Currently, there
 * is no cross-platform method to load chunks in SSR. Some TPAs support
 * Loadable, but the editor-elements do not support any lazy loading in SSR.
 * Consequently, there is no general solution until React 18 is universally
 * adopted and Suspense is supported in SSR. To address the SSR translation
 * issue, we use the following approach:
 * 1. Only five translation keys are directly visible to the users. Therefore,
 * we prepare a JSON file with all the translations for these keys (see
 * vitalTranslations).
 * 2. It's 3KB gzipped— not great, not terrible.
 * 3. In CSR, we load the full translations for the requested locale.
 *
 */
const vitalTranslationsWithDefaults = addDefaultsFromEnglish(viewerVitalTranslations);

export const TranslatedContentViewer: React.FC<Omit<ContentViewerProps, 't'>> = props => {
  const locale = props.locale;

  const [resourcesState, setResourcesState] = React.useState(() => {
    return {
      status: 'READY' as 'READY' | 'LOADING' | 'ERROR',
      loadedLocales: ['en'],
      i18nextInstance: createI18nextInstance({
        activeLocale: locale,
        resources: vitalTranslationsWithDefaults,
      }),
    };
  });

  const { i18nextInstance, loadedLocales } = resourcesState;

  if (locale !== i18nextInstance.language) {
    // This is done outside of the effect because locale changes might not
    // trigger state changes thus the rendering with updated i18nextInstance
    // will not happen. We can add 'locale's copy to state, but then we will be jugling 3 sources of truth for locale
    i18nextInstance.changeLanguage(locale);
  }

  React.useEffect(() => {
    if (loadedLocales.includes(locale) || resourcesState.status !== 'READY') {
      // If load only unloaded locales and only in 'IDLE' status
      // If the status is 'LOADING', but at the end the loaded locale is not in loadedLocales, it will be loaded then
      // If the status is 'ERROR', we won't try again to prevent infinite loop.
      return;
    }

    // The rest is resource loading

    setResourcesState(prevState => ({ ...prevState, status: 'LOADING' }));
    import(
      /* webpackChunkName: "ricos_viewer_messages_[request]" */
      `../translations/locale/messages_${locale}.json`
    )
      .then(response => response.default)
      .then(localeResource => {
        i18nextInstance.addResourceBundle(locale, 'translation', localeResource, true, true);
        setResourcesState(prevState => {
          return {
            ...prevState,
            status: 'READY',
            loadedLocales: [...prevState.loadedLocales, locale],
          };
        });
      })
      .catch(err => {
        console.error(`can't find ${locale} locale`, err);
        setResourcesState(prevState => {
          return { ...prevState, status: 'ERROR' };
        });
      });
  }, [locale, loadedLocales]);

  const t = React.useMemo(() => i18nextInstance.t.bind(i18nextInstance), [i18nextInstance]);

  return <ContentViewer {...props} t={t} />;
};

function createI18nextInstance({
  activeLocale,
  resources,
}: {
  activeLocale: string;
  resources: { [key: string]: LocaleResource };
}) {
  const resourcesWithNs = Object.keys(resources).reduce<Resource>((acc, key) => {
    acc[key] = { translation: resources[key] };
    return acc;
  }, {});

  const instance = i18next.createInstance({
    lng: activeLocale,
    keySeparator: '$',
    interpolation: { escapeValue: false },
    resources: resourcesWithNs,
  });
  instance.init();
  return instance;
}

type StringResources = {
  [locale: string]: { [key: string]: string };
};

function addDefaultsFromEnglish(resources: StringResources) {
  const english = resources.en;

  return Object.keys(resources).reduce<StringResources>((acc, locale) => {
    acc[locale] = { ...english, ...resources[locale] };
    return acc;
  }, {});
}
